import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Gift, ArrowRight } from "lucide-react";

const DISMISS_KEY = "platebuddies:urgency-banner:dismissed:v3";

/**
 * Top-of-site urgency strip.
 *
 * Purpose: give warm scarcity ("founding families") + a real discount code so
 * a bouncing visitor has one clear reason to check out. The code is a real
 * one — `WELCOME10` is defined in the backend `COUPONS` dict.
 *
 * Sticky above nav on desktop, tappable target on mobile. Dismisses persistently
 * so returning visitors don't see the same nudge twice. Bump `DISMISS_KEY`
 * suffix to force re-show after a campaign refresh.
 */
export default function UrgencyBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY) === "1";
      setVisible(!dismissed);
    } catch {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* private-mode users just re-see the banner next visit — fine. */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="urgency-banner"
      className="relative z-50 text-white text-xs sm:text-sm font-bold overflow-hidden"
      style={{
        background:
          "linear-gradient(90deg, #F26A34 0%, #FF9A6C 45%, #FFB74D 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 sm:py-2.5 flex items-center justify-center gap-2 sm:gap-4 text-center pr-10">
        <Gift className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
        <span className="hidden sm:inline">Founding families launch:</span>
        <span>
          <b className="font-extrabold">10% off any order</b> with code{" "}
          <code
            data-testid="urgency-banner-code"
            className="bg-white/25 rounded px-1.5 py-0.5 font-mono tracking-widest text-white font-extrabold"
          >
            WELCOME10
          </code>
        </span>
        <Link
          to="/shop?coupon=WELCOME10"
          data-testid="urgency-banner-cta"
          className="hidden sm:inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest transition"
        >
          Shop kits <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss offer"
        data-testid="urgency-banner-dismiss"
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center transition"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
